import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Mail, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/layout/Logo";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { useSession } from "@/stores/session";
import { isFirebaseConfigured, signInWithGoogle, signInWithEmail } from "@/lib/firebase";
import { cn } from "@/lib/cn";

function makeId() {
  try { return crypto.randomUUID(); } catch {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

const SOCIAL_PROOF = [
  { value: "47,200", label: "students" },
  { value: "+87 pts", label: "median lift" },
  { value: "94%", label: "would recommend" },
  { value: "4.9★", label: "user rating" },
];

export function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn } = useSession();
  const nav = useNavigate();

  // Demo fallback when Firebase isn't configured.
  const finishDemo = (name?: string) => {
    signIn({
      id: makeId(),
      name: name ?? email.split("@")[0] ?? "Student",
      email: email || `${(name ?? "student").toLowerCase().replace(/\s/g, "")}@thinkbigtechies.app`,
      plan: "free",
    });
    nav("/app/learn");
  };

  const finishWith = (u: { id: string; name: string; email: string }) => {
    signIn({ ...u, plan: "free" });
    nav("/app/learn");
  };

  const google = async () => {
    setError(null);
    if (!isFirebaseConfigured) return finishDemo("Student");
    try {
      finishWith(await signInWithGoogle());
    } catch (e) {
      setError("Google sign-in failed. Try again.");
    }
  };

  const emailAuth = async () => {
    setError(null);
    if (!isFirebaseConfigured) return finishDemo();
    if (!email || !password) {
      setError("Enter your email and a password (6+ characters).");
      return;
    }
    try {
      finishWith(await signInWithEmail(email, password));
    } catch {
      setError("Could not sign in. Check your details and try again.");
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-background text-foreground">
      {/* Top bar */}
      <div className="absolute inset-x-0 top-0 z-20 flex h-16 items-center justify-between px-6 md:px-12">
        <Link to="/app/learn"><Logo /></Link>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <Link to="/app/learn" className="text-sm text-muted-foreground hover:text-foreground">
            Back to home
          </Link>
        </div>
      </div>

      {/* Background washes */}
      <div className="pointer-events-none absolute -top-32 -left-32 size-[480px] rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 size-[480px] rounded-full bg-accent/10 blur-3xl" />

      {/* Main content */}
      <main className="relative z-10 mx-auto flex min-h-screen max-w-6xl flex-col justify-center px-6 py-24 md:px-12">
        <div className="grid items-center gap-16 lg:grid-cols-[1.1fr_1fr]">
          {/* Editorial hero — the 705+ */}
          <div className="space-y-10">
            <div className="space-y-3">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Think Big Techies · AI GMAT Tutoring
              </div>
              <h1 className="text-balance text-5xl font-display font-medium leading-[1.05] tracking-tight md:text-6xl">
                Your path to a
              </h1>
              <div className="flex items-baseline gap-4">
                <span className="font-serif text-[180px] font-normal leading-[0.85] tracking-tight md:text-[240px]">
                  705
                </span>
                <span className="font-serif text-6xl font-normal text-primary md:text-7xl">+</span>
              </div>
              <h2 className="text-balance text-3xl font-display font-light text-muted-foreground md:text-4xl">
                starts with a conversation.
              </h2>
            </div>

            <p className="max-w-md text-pretty text-base leading-relaxed text-muted-foreground">
              Maya is your personal AI tutor — she sees what you're stuck on,
              explains it the way it finally clicks, and remembers every weak spot
              between sessions. Not flashcards. A real tutor.
            </p>

            {/* Social proof bar */}
            <div className="grid grid-cols-2 gap-x-8 gap-y-4 md:grid-cols-4">
              {SOCIAL_PROOF.map((s) => (
                <div key={s.label}>
                  <div className="font-serif text-3xl font-medium text-foreground md:text-4xl">
                    {s.value}
                  </div>
                  <div className="mt-1 text-xs uppercase tracking-wider text-muted-foreground">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Auth panel */}
          <div className="relative">
            <div className={cn(
              "relative rounded-3xl border border-border bg-card p-8 shadow-2xl shadow-foreground/5",
              "md:p-10",
            )}>
              <div className="mb-6 space-y-1">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Sign in</div>
                <div className="font-display text-2xl font-medium">Welcome back</div>
              </div>

              {!showEmail ? (
                <div className="space-y-3">
                  <AuthButton
                    onClick={google}
                    primary
                    icon={
                      <svg viewBox="0 0 24 24" className="size-5" fill="currentColor">
                        <path d="M21.35 11.1H12v2.96h5.36c-.23 1.23-1.45 3.6-5.36 3.6-3.22 0-5.85-2.67-5.85-5.96s2.63-5.96 5.85-5.96c1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.74 3.06 14.59 2 12 2 6.48 2 2 6.48 2 12s4.48 10 10 10c5.77 0 9.6-4.05 9.6-9.76 0-.66-.07-1.16-.25-1.74z" />
                      </svg>
                    }
                    label="Continue with Google"
                  />
                  <div className="relative my-3 flex items-center gap-3">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs uppercase tracking-wider text-muted-foreground">or</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <AuthButton
                    onClick={() => setShowEmail(true)}
                    icon={<Mail className="size-5" />}
                    label="Continue with email"
                  />
                </div>
              ) : (
                <form onSubmit={(e) => { e.preventDefault(); void emailAuth(); }} className="space-y-3">
                  <Input
                    autoFocus
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 text-base"
                  />
                  {isFirebaseConfigured && (
                    <Input
                      type="password"
                      placeholder="Password (6+ characters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 text-base"
                    />
                  )}
                  <Button type="submit" variant="default" size="lg" className="w-full justify-center">
                    {isFirebaseConfigured ? "Sign in / Sign up" : "Continue"} <ArrowRight className="size-4" />
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowEmail(false)}
                    className="block w-full text-center text-sm text-muted-foreground hover:text-foreground"
                  >
                    ← Use another sign-in method
                  </button>
                </form>
              )}

              {error && (
                <p className="mt-3 text-center text-xs text-destructive">{error}</p>
              )}

              {/* Pre-emption pill */}
              <div className="mt-6 flex items-center justify-center gap-2 rounded-full border border-border bg-secondary/50 px-4 py-2 text-xs">
                <Sparkles className="size-3.5 text-primary" />
                <span className="font-medium">Voice AI tutor</span>
                <span className="text-muted-foreground">included on all plans</span>
              </div>

              <p className="mt-6 text-center text-[11px] leading-relaxed text-muted-foreground">
                By continuing you agree to our Terms of Service and Privacy Policy.
                <br /> 7-day free trial. Cancel anytime.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function AuthButton({
  onClick, icon, label, primary,
}: { onClick: () => void; icon: React.ReactNode; label: string; primary?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex h-12 w-full items-center justify-center gap-3 rounded-xl text-sm font-medium transition-colors",
        primary
          ? "bg-foreground text-background hover:bg-foreground/90"
          : "border border-border bg-background hover:bg-secondary",
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
